import { LIST_TYPES } from 'constants/listTypes';
import { selectFeatureFlags } from 'core/userContext';
import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import { getIsEligibilityCampaign } from 'modules/campaigns/editor/components/utils';
import { useCampaignContext } from 'modules/campaigns/editor/context';
import { GraphQLAudienceList, useQueryListsSizes } from 'modules/campaigns/editor/hooks';
import { ListAction } from 'modules/campaigns/editor/types';
import { AudienceList } from 'modules/domains/campaign';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { Badge, BADGE_VARIANTS } from 'ui/badge';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { Link } from 'ui/links';
import { Radio } from 'ui/radio';
import { CircleSpinner } from 'ui/spinner';
import { body2 } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import {
  Cell,
  CheckboxCell,
  ContactsCell,
  Description,
  ListLastModifiedCell,
  NameCell,
  RadioCell,
  TABLE_GRID,
  TABLE_GRID_ELIGIBILITY,
  TableRow,
} from '../../AudienceSelection.styled';
import { useAudienceContext } from '../../context';
import { getListSizeDisplayText } from './AudienceListSummary/utils';

export const AUDIENCE_TABLE_ROW_TEST_ID = 'audience-table-row';
export const LIST_ID_CELL_TEST_ID = 'list-id';
export const LOADING_LIST_SIZE_SPINNER_TEST_ID = 'loading-list-size-spinner';
export const LIST_LINK_TEST_ID = 'list-link';

const ELIGIBILITY_DISABLED_INFO =
  "This list can't be used for embedded messaging eligibility. Choose a dynamic list that only references user profile fields and static list membership.";

type AudienceSelectionTableRowProps = {
  list: GraphQLAudienceList;
};

export const AudienceSelectionTableRow: FC<
  React.PropsWithChildren<AudienceSelectionTableRowProps>
> = ({ list }) => {
  const canFetchListSize = !useSelector(selectFeatureFlags).DisableListsSizeQueryFetch;
  const {
    id,
    name,
    description,
    listType,
    createdAt,
    updatedAt,
    creatorUserId,
    hasUnsupportedEmbeddedEventType,
    hasUnsupportedEmbeddedListReference,
  } = list;
  const { toggleListSelection, selectedSendLists, selectedSuppressionLists, listCreatorNames } =
    useAudienceContext();
  const { campaign } = useCampaignContext();
  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();
  const isEligibility = getIsEligibilityCampaign(campaign.campaignTypeDefinition);

  const gridLayout = isEligibility ? TABLE_GRID_ELIGIBILITY : TABLE_GRID;

  const checkboxStatus = (selectedLists: AudienceList[]) =>
    selectedLists?.some((val) => val.id === id)
      ? CHECKBOX_STATUS.CHECKED
      : CHECKBOX_STATUS.UNCHECKED;
  // Lists can't be set as both Send and Suppress, check that the selected list hasn't already been attributed to a different list action
  const getIsDisabled = (oppositeSelectedLists: AudienceList[]) =>
    oppositeSelectedLists?.some((val) => val.id === id);
  const isEligibilityDisabled = !!(
    hasUnsupportedEmbeddedEventType || hasUnsupportedEmbeddedListReference
  );

  const [{ isLoading, size }] = useQueryListsSizes([{ id, name }]);

  return (
    <TableRow gridLayout={gridLayout} data-test={AUDIENCE_TABLE_ROW_TEST_ID}>
      {!isEligibility && (
        <>
          <CheckboxCell
            id={ListAction.Send}
            handleClick={() =>
              toggleListSelection(ListAction.Send, {
                id: list.id,
                name: list.name,
              })
            }
            status={checkboxStatus(selectedSendLists)}
            isDisabled={
              getIsDisabled(selectedSuppressionLists) || listType === LIST_TYPES.SUPPRESSION
            }
          />
          <CheckboxCell
            id={ListAction.Suppress}
            handleClick={() =>
              toggleListSelection(ListAction.Suppress, {
                id: list.id,
                name: list.name,
              })
            }
            status={checkboxStatus(selectedSuppressionLists)}
            isDisabled={getIsDisabled(selectedSendLists)}
          />
        </>
      )}
      {isEligibility && (
        <RadioCell id={ListAction.Eligible}>
          <Tooltip isEnabled={isEligibilityDisabled} tooltipContent={ELIGIBILITY_DISABLED_INFO}>
            <Radio
              data-test="radio-grid-cell-radio"
              id={`${ListAction.Eligible}-radio-cell`}
              onClick={() =>
                toggleListSelection(ListAction.Eligible, {
                  id: list.id,
                  name: list.name,
                })
              }
              isDisabled={isEligibilityDisabled}
              isChecked={checkboxStatus(selectedSendLists) === CHECKBOX_STATUS.CHECKED}
              value={checkboxStatus(selectedSendLists) === CHECKBOX_STATUS.CHECKED}
            />
          </Tooltip>
        </RadioCell>
      )}
      <NameCell>
        <Link
          data-test={LIST_LINK_TEST_ID}
          href={`/segmentation?emailListId=${id}`}
          useReactRouter={isModernizedSegmentationPageEnabled}
        >
          {name}
        </Link>
        {description && <Description>{description}</Description>}
      </NameCell>
      <Cell data-test={LIST_ID_CELL_TEST_ID}>{id}</Cell>
      <Cell>
        <Badge variant={BADGE_VARIANTS.INFO}>{listType}</Badge>
      </Cell>
      {canFetchListSize && (
        <ContactsCell>
          {isLoading ? (
            <CircleSpinner size={body2.fontSize} data-test={LOADING_LIST_SIZE_SPINNER_TEST_ID} />
          ) : (
            getListSizeDisplayText(size)
          )}
        </ContactsCell>
      )}
      <ListLastModifiedCell
        createdAt={createdAt}
        createdBy={{ emailAddress: creatorUserId, fullName: listCreatorNames[creatorUserId] }}
        modifiedAt={updatedAt}
        showAvatar={false}
      />
    </TableRow>
  );
};
